<template>
  <div class="change-password-wrapper">
    <v-list
      class="pt-0"
      two-line
    >
      <div class="fixed-header ml-2 mt-3 ">
        <v-row class="px-5 ">
          <v-col
            cols="10"
          >
            <h2>Change Password</h2>
          </v-col>
          <v-col
            cols="2"
            class="d-flex justify-end pr-0"
          >
            <v-btn
              id="close-drawer-btn"
              class="text-right"
              icon
              @click.stop="closeDrawer()"
            >
              <v-icon
                medium
              >
                mdi-close
              </v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-row class="px-5 ">
          <v-col
            cols="12"
            class="pt-0 font-weight-regular text--black"
            style="opacity:0.6"
          >
            To ensure your information is secure, please choose a password that is unique and difficult to guess.
          </v-col>
        </v-row>
        <v-row class="px-5">
          <v-col
            class="mb-2"
            cols="11"
          >
            <v-btn
              :loading="loading"
              @click.stop="saveNewPassword"
              id="save-new-password-btn"
              :dark="enableSavePassword"
              :disabled="!enableSavePassword"
            >
              SAVE
            </v-btn>
          </v-col>
        </v-row>
      </div>
      <div class="ml-2">
        <v-row class="px-5">
          <v-col cols="11">
            <v-form
              v-model="validCurrentPassword"
              ref="validCurrentPassword"
              lazy-validation
            >
              <v-text-field
                id="current-password"
                ref="currentPassword"
                v-model.trim="passwords.currentPassword.value"
                label="Current Password *"
                outlined
                required
                persistent-hint
                class="current-password mb-0"
                :rules="requiredRule"
                validate-on-blur
                :append-icon="passwords.currentPassword.masked ? 'visibility' : 'visibility_off'"
                :type="passwords.currentPassword.masked ? 'text' : 'password'"
                @keyup.enter="saveNewPassword"
                @click:append="passwords.currentPassword.masked = !passwords.currentPassword.masked"
              />
            </v-form>
            <div
              class="pt-0 mt-0 body-2 text--black ml-4"
              style="opacity:0.6"
            >
              We need your current password to confirm your changes
            </div>
          </v-col>
        </v-row>
        <v-row class="px-5">
          <v-col cols="11">
            <v-form
              v-model="validNewPassword"
              ref="validNewPassword"
            >
              <v-text-field
                id="new-password"
                ref="newPassword"
                v-model="passwords.newPassword.value"
                label="New Password *"
                outlined
                required
                persistent-hint
                :error-messages="newPasswordRule"
                :error="passwords.newPassword.error"
                @blur="passwords.newPassword.value.length ? $v.passwords.newPassword.value.$touch() : null"
                :rules="[rules.required, rules.strongPassword]"
                :append-icon="passwords.newPassword.masked ? 'visibility' : 'visibility_off'"
                :type="passwords.newPassword.masked ? 'text' : 'password'"
                :messages="passwords.newPassword.messages"
                @click:append="passwords.newPassword.masked = !passwords.newPassword.masked"
                @keyup.enter="saveNewPassword"
                @input="enableSavePassword = true"
              />
            </v-form>
          </v-col>
        </v-row>
        <v-row class="px-5">
          <v-col cols="11">
            <v-form
              v-model="validConfirmNewPassword"
              ref="validConfirmNewPassword"
            >
              <v-text-field
                id="confirm-new-password"
                ref="confirmNewPassword"
                v-model="passwords.confirmNewPassword.value"
                label="Confirm New Password *"
                outlined
                required
                persistent-hint
                :error-messages="confirmNewPasswordRule"
                @blur="passwords.confirmNewPassword.value.length ? $v.passwords.confirmNewPassword.value.$touch() : null"
                :rules="[rules.required, rules.strongPassword]"
                :append-icon="passwords.confirmNewPassword.masked ? 'visibility' : 'visibility_off'"
                :type="passwords.confirmNewPassword.masked ? 'text' : 'password'"
                :error="passwords.confirmNewPassword.error"
                :messages="passwords.confirmNewPassword.messages"
                @click:append="passwords.confirmNewPassword.masked = !passwords.confirmNewPassword.masked"
                @keyup.enter="saveNewPassword"
                @input="enableSavePassword = true"
              />
            </v-form>
          </v-col>
        </v-row>
        <v-row class="px-5">
          <v-col cols="10">
            <div
              v-if="errorMessage != ''"
              class="caption red--text"
            >
              {{ errorMessage }}
            </div>
            <v-row
              v-for="(rule,index) in passwordRules"
              :key="index"
              class="subtitle-1 text-left login-grey--text"
            >
              <v-col
                cols="auto"
                class="pr-0 py-0"
              >
                <v-icon
                  class="body-1"
                  :color="iconColor(rule)"
                >
                  {{ rule.isValid !== false ? 'mdi-check-circle' : 'mdi-close-circle' }}
                </v-icon>
              </v-col>
              <v-col class="py-0">
                <span :style="'font-weight:400;font-size:12px;color:'+textColor(rule)">{{ rule.text }}</span>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="1" />
        </v-row>
      </div>
    </v-list>
  </div>
</template>
<script>
  import { $selfService } from '@/services'
  import { validationMixin } from 'vuelidate'
  import { required } from 'vuelidate/lib/validators'

  export default {
    name: 'ChangePasswordDrawer',
    mixins: [validationMixin],
    data () {
      return {
        accountId: '',
        loading: false,
        enableSavePassword: false,
        passwordRules: [
          { text: 'Password must be at least 8 characters long', isValid: '' },
          { text: 'Password must contain at least one upper case character', isValid: '' },
          { text: 'Password must contain at least one special characters (-,+!)', isValid: '' },
          { text: 'Password must contain at least one number', isValid: '' },
          { text: 'Password cannot have same character consecutively', isValid: '' },
        ],
        errorMessage: '',
        validCurrentPassword: true,
        validNewPassword: true,
        validConfirmNewPassword: true,
        requiredRule: [
          v => !!v || 'Required',
          v => !v || !!v.trim() || 'Required'],
        rules: {
          required: value => {
            this.passwords.currentPassword.error = false
            this.passwords.newPassword.error = false
            this.passwords.confirmNewPassword.error = false
            this.passwords.newPassword.messages = ''
            this.passwords.currentPassword.messages = ''
            this.passwords.confirmNewPassword.messages = ''
            return !!value || "Required";
          },
          match: value => value === this.passwords.newPassword.value || 'Password does not match.',
          strongPassword: value => {
            if (value.length === 0) {
              this.passwordRules[0].isValid = ''
              this.passwordRules[1].isValid = ''
              this.passwordRules[2].isValid = ''
              this.passwordRules[3].isValid = ''
              this.passwordRules[4].isValid = ''
              return false
            };
            let flag = true
            this.passwordRules[0].isValid = true
            if (!(/.{8,}/).test(value)) {
              this.passwordRules[0].isValid = false
              flag = false
            }

            this.passwordRules[1].isValid = true
            if (!(/[A-Z]+/).test(value)) {
              this.passwordRules[1].isValid = false
              flag = false
            }

            this.passwordRules[2].isValid = true
            if (!(/(?=.*?[#?!@$%^&*-])+/).test(value)) {
              this.passwordRules[2].isValid = false
              flag = false
            }

            this.passwordRules[3].isValid = true
            if (!(/[0-9]+/).test(value)) {
              this.passwordRules[3].isValid = false
              flag = false
            }

            this.passwordRules[4].isValid = true
            if ((/^\S*(.)\1\S*/).test(value)) {
              this.passwordRules[4].isValid = false
              flag = false
            }
            return flag
          },
        },
        passwords: {
          currentPassword: {
            value: '',
            error: false,
            messages: '',
            masked: false,
          },
          newPassword: {
            value: '',
            error: false,
            messages: '',
            masked: false,
          },
          confirmNewPassword: {
            value: '',
            error: false,
            messages: '',
            masked: false,
          },
        },
      }
    },
    validations: {
      passwords:{
       currentPassword :{
         value:{ required }
       },
       newPassword :{
         value:{ required }
       },
       confirmNewPassword :{
         value:{ required }
       },
      },
    },
    computed: {
      currentPasswordRule() {
        const errors = []
        if (!this.$v.passwords.currentPassword.value.$dirty) return errors
        !this.$v.passwords.currentPassword.value.required && errors.push('Required')
        return errors
      },
      newPasswordRule() {
        const errors = []
        if (!this.$v.passwords.newPassword.value.$dirty) return errors
        !this.$v.passwords.newPassword.value.required && errors.push('Required')
        return errors
      },
      confirmNewPasswordRule() {
        const errors = []
        if (!this.$v.passwords.confirmNewPassword.value.$dirty) return errors
        !this.$v.passwords.confirmNewPassword.value.required && errors.push('Required')
        return errors
      }
    },
    mounted () {
      if (this.$store.state.auth.editAccountId) {
        this.accountId = this.$store.state.auth.editAccountId
      } else {
        this.accountId = this.$store.state.auth.account.id
      }
    },
    methods: {
      iconColor (rule) {
        return rule.isValid === '' ? 'transparent' : (rule.isValid ? '#1B5E20' : '#B00020')
      },
      textColor (rule) {
        return rule.isValid === '' ? 'rgba(0,0,0,0.6)' : (rule.isValid ? 'rgba(0,0,0,0.87)' : '#B00020')
      },
      closeDrawer () {
        this.$store.commit('ui/drawer.right', {
          show: false,
          component: null,
          response: { checkProfileUpdateInfo: true },
        })
      },
      saveNewPassword (e) {
        if(!this.$refs.validCurrentPassword.validate()) return
        this.$v.$touch()
        if(this.$refs.validCurrentPassword.validate() &&
          this.$refs.validNewPassword.validate() &&
          this.$refs.validConfirmNewPassword.validate() &&
          this.rules.strongPassword(this.passwords.newPassword.value)
        ) {
          if(this.passwords.newPassword.value === this.passwords.confirmNewPassword.value){
            this.loading = true
            const data = {
              user: {
                current_password: this.passwords.currentPassword.value,
                password: this.passwords.newPassword.value,
                password_confirmation: this.passwords.confirmNewPassword.value,
              },
            }
            $selfService.saveProfileData(this.accountId, data).then(response => {
              this.$store.commit('ui/snackbar', { message: 'Password changed successfully', type: 'success' })
              this.closeDrawer()
            }).finally(() => {
              this.loading = false
            })
          }
          else{
            this.passwords.newPassword.error = true
            this.passwords.newPassword.messages = 'Password does not match.'
            this.passwords.confirmNewPassword.error = true
            this.passwords.confirmNewPassword.messages = 'Password does not match.'
          }      
        } else if (this.passwords.newPassword.value === '' || this.passwords.confirmNewPassword.value === '') {
          this.passwordRules[0].isValid = false
          this.passwordRules[1].isValid = false
          this.passwordRules[2].isValid = false
          this.passwordRules[3].isValid = false
          this.passwordRules[4].isValid = false
        } else if (!this.rules.strongPassword(this.passwords.newPassword.value)
        ) {
          this.passwords.newPassword.error = true
          this.passwords.newPassword.messages = 'Enter Valid New Password'
        }
      },
    },
  }
</script>
